import React, { useEffect, useState } from 'react';
import { Dropdown, FormControl, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

/**
 * @param Object params - { page: String, query: String }
 * @param function setParams(params) - sets the params in the parent component
 * @param {Array} filters - [name: String, label: String, type: 'text' | 'select', options: [String]]
 * @
 *
 * Renders a filter component which contains field for each filter.
 * Changing the value of the filter will update the params in the parent component.
 * 
 * For select filters, we need to pass options as key strings as we translate them inside select component.
 */
export default function Filters(_ref) {
  let {
    params,
    setParams,
    filters
  } = _ref;
  const {
    t
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const onFilterToggle = (isOpen, metadata) => {
    if (metadata.target.localName === 'input' || metadata.target.localName === 'select') return;
    setIsOpen(isOpen);
  };

  // open filter menu if there are params in the url
  useEffect(() => {
    if (!!params.query) {
      setIsOpen(true);
    }
  }, [params.query]);

  // Dropdown needs access to the DOM node in order to position the Menu
  const CustomToggle = /*#__PURE__*/React.forwardRef((_ref2, ref) => {
    let {
      children,
      onClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Button, {
      ref: ref,
      onClick: e => {
        e.preventDefault();
        onClick(e);
      }
    }, children, /*#__PURE__*/React.createElement("span", {
      className: "material-icons md-18 ms-2"
    }, isOpen ? 'remove' : 'add'));
  });

  // Dropdown needs access to the DOM of the Menu to measure it
  const CustomMenu = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
    let {
      children,
      style,
      className,
      'aria-labelledby': labeledBy
    } = _ref3;
    const [filterValues, setFilterValues] = useState({});
    const handleFilterChange = (column, value) => {
      setFilterValues(prevFilters => ({
        ...prevFilters,
        [column]: encodeURIComponent(value)
      }));
    };
    const onFilterSubmit = () => {
      setParams({
        ...params,
        query: new URLSearchParams(filterValues).toString()
      });
    };
    const onFilterClear = () => {
      setFilterValues({});
      setParams({
        ...params,
        query: ''
      });
    };
    const getFilterFormControl = filter => {
      switch (filter.type) {
        case 'select':
          return /*#__PURE__*/React.createElement(FormControl, {
            as: "select",
            className: "mx-3 my-2 w-auto",
            onChange: e => handleFilterChange(filter.name, e.target.value),
            value: decodeURIComponent(filterValues[filter.name] || '')
          }, /*#__PURE__*/React.createElement("option", {
            value: ""
          }), filter.options.map((option, index) => {
            return /*#__PURE__*/React.createElement("option", {
              key: index,
              value: option
            }, t(option));
          }));
        default:
          return /*#__PURE__*/React.createElement(FormControl, {
            className: "mx-3 my-2 w-auto",
            onChange: e => handleFilterChange(filter.name, e.target.value),
            value: decodeURIComponent(filterValues[filter.name] || '')
          });
      }
    };

    // if there are params in url, set the filter values to the params
    useEffect(() => {
      if (params.query) {
        const query = new URLSearchParams(params.query);
        const filters = {};
        for (const [key, value] of query.entries()) {
          filters[key] = value;
        }
        setFilterValues(filters);
      }
    }, [params]);
    return /*#__PURE__*/React.createElement("div", {
      ref: ref,
      style: style,
      className: className,
      "aria-labelledby": labeledBy
    }, /*#__PURE__*/React.createElement(Button, {
      className: "float-end",
      variant: "link",
      style: {
        'width': '100px'
      },
      onClick: () => onFilterClear()
    }, "Clear All"), /*#__PURE__*/React.createElement("div", {
      className: "d-flex flex-column"
    }, filters.map((filter, index) => {
      return /*#__PURE__*/React.createElement("div", {
        key: index,
        className: "d-flex flex-column mt-2"
      }, /*#__PURE__*/React.createElement("label", {
        className: "font-weight-bold"
      }, filter.label), getFilterFormControl(filter));
    })), /*#__PURE__*/React.createElement(Button, {
      className: "float-center",
      variant: "primary",
      style: {
        'width': '100px'
      },
      onClick: () => onFilterSubmit()
    }, "Submit"));
  });
  return /*#__PURE__*/React.createElement(Dropdown, {
    show: isOpen,
    onToggle: (isOpen, metadata) => onFilterToggle(isOpen, metadata)
  }, /*#__PURE__*/React.createElement(Dropdown.Toggle, {
    as: CustomToggle,
    id: "dropdown-custom-components"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons md-18 me-2"
  }, "tune"), " Filters"), /*#__PURE__*/React.createElement(Dropdown.Menu, {
    as: CustomMenu,
    className: "filters-menu d-flex flex-column"
  }));
}